import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Checkbox,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import Image from 'src/components/Image';
import ListActionBar from 'src/components/ListActionBar';
import StatusButton from 'src/components/StatusButton';
import TableHeadTypography from 'src/components/TableHeadTypography';
import TableContainer from 'src/components/Table/TableContainer';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import HighlightedText from 'src/components/HighlightedText';
import { TableCellTypography } from 'src/components/GenericTableCell';
import useTypographyMediaQuery from 'src/hooks/useTypographyMediaQuery';
import useSubscribeStorage from 'src/hooks/useSubscribeStorage';
import { useTagOptions } from 'src/hooks/tag';
import { WDAC_RESULTS_QUERY_KEY } from 'src/hooks/wdac';
import { DEFAULT_ENVIRONMENT_ID } from 'src/constants/storage';
import { PORTAL_ROUTE } from 'src/constants/routes';
import { pagesText } from 'src/constants/pagesText';
import WDACResultsService from 'src/services/wdac-results.service';
import {
  APPLICATION_TRUST_STATUS,
  getApplicationTrustStatusLabel,
  getApplicationTrustStatusOptions,
} from 'src/utils/enums/applicationTrustStatus';
import WarningIcon from 'src/components/WarningIcon';
import UpdateStatusModal from 'src/features/appStatus/UpdateStatusModal';
import { getCatalogWarning } from './catalogWarning';
import useUpdateWDACStatus from './useUpdateWDACStatus';
import Win32AppStatusModal from './Win32AppStatusModal';
import {
  WIN32_APPS_COLUMNS,
  WIN32_APPS_COLUMN_GROUPS,
  WDAC_APP_PREVIOUS_PATH,
  getWin32StatusNote,
} from './constants';

const STATUS_OPTIONS = getApplicationTrustStatusOptions();

const CLOSED_STATUS_MODAL = { open: false, rows: [] };

// Building a trusted app's artifacts is done one app at a time, so a bulk trust records the status
// alone and says so.
const getBulkTrustWarning = status =>
  status === APPLICATION_TRUST_STATUS.Trusted
    ? "Trusting apps in bulk doesn't build anything: no file set imports are changed and no security catalogs are generated. Open each app and update its catalog to build its trusted artifacts."
    : null;

const GROUP_DIVIDER_SX = { borderRight: '2px solid #2E3236' };
const GROUP_ROW_HEIGHT = 30;

// Names and publishers need the room; the count columns only ever hold small integers.
const COLUMN_WEIGHTS = {
  status: 1.7,
  arpDisplayName: 2.4,
  arpPublisher: 1.6,
  managedApplicationMatchCount: 1.6,
};

const ColumnHeader = ({ label, tooltip, sortable, sortActive, sortDirection, onSort }) => {
  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();

  const row = (
    <Stack
      direction="row"
      gap={1}
      alignItems="center"
      onClick={sortable ? onSort : undefined}
      sx={{ cursor: sortable ? 'pointer' : tooltip ? 'help' : 'default' }}>
      <TableHeadTypography
        hasSorting={sortActive}
        mediumScreen={mediumScreen}
        largeScreen={largeScreen}
        smallScreen={smallScreen}>
        {label}
      </TableHeadTypography>
      {sortable ? (
        <Iconify
          icon={
            sortActive
              ? sortDirection === 'asc'
                ? 'mdi:arrow-up'
                : 'mdi:arrow-down'
              : 'mdi:swap-vertical'
          }
          width={14}
          height={14}
          color={sortActive ? '#7DFF92' : '#9CA3AF'}
        />
      ) : null}
    </Stack>
  );

  return tooltip ? (
    <Tooltip
      title={tooltip}
      arrow
      componentsProps={{ tooltip: { sx: { bgcolor: '#3F3F3F', maxWidth: 320 } } }}>
      <Box component="span" display="inline-flex">
        {row}
      </Box>
    </Tooltip>
  ) : (
    row
  );
};

const CountCell = ({ value }) => (
  <TableCell align="center" sx={{ py: 0.5 }}>
    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
      {Number(value || 0).toLocaleString()}
    </Typography>
  </TableCell>
);

const ELLIPSIS_SX = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
  maxWidth: '100%',
};

const ResultsTable = ({
  isLoading,
  results,
  sentryRef,
  isFetchingNextPage,
  totalCount,
  query,
  searchValue,
  onSearchChange,
  listFilters,
  onListFiltersChange,
  sort,
  onSortChange,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const { data: tagOptionsData } = useTagOptions({ search: '', enabled: true });
  const tagOptions = useMemo(() => tagOptionsData?.tags || [], [tagOptionsData]);

  const columnWidths = useMemo(() => {
    const getWeight = key => COLUMN_WEIGHTS[key] ?? 1;
    const totalParts = WIN32_APPS_COLUMNS.reduce((sum, column) => sum + getWeight(column.key), 0);
    return Object.fromEntries(
      WIN32_APPS_COLUMNS.map(column => [
        column.key,
        `${(getWeight(column.key) / totalParts) * 100}%`,
      ]),
    );
  }, []);

  const selectedStatuses = useMemo(
    () =>
      listFilters?.applicationTrustStatuses || [
        APPLICATION_TRUST_STATUS.Unknown,
        APPLICATION_TRUST_STATUS.Trusted,
        APPLICATION_TRUST_STATUS.InProgress,
      ],
    [listFilters],
  );
  const selectedTagId = useMemo(() => listFilters?.tagId || '', [listFilters]);
  const warningRowsOnly = useMemo(() => listFilters?.warningRowsOnly ?? true, [listFilters]);
  const canUseWarningFilter = useMemo(
    () => selectedStatuses.includes(APPLICATION_TRUST_STATUS.Trusted),
    [selectedStatuses],
  );

  const filters = useMemo(
    () => ({
      applicationTrustStatuses: selectedStatuses,
      tagId: selectedTagId,
      warningRowsOnly: warningRowsOnly && canUseWarningFilter ? [true] : [],
    }),
    [selectedStatuses, selectedTagId, warningRowsOnly, canUseWarningFilter],
  );

  const filterOptions = useMemo(
    () => [
      {
        key: 'applicationTrustStatuses',
        type: 'checkbox',
        label: 'Trust Status',
        options: Object.values(APPLICATION_TRUST_STATUS).map(value => ({
          value,
          label: getApplicationTrustStatusLabel(value),
        })),
      },
      ...(canUseWarningFilter
        ? [
            {
              key: 'warningRowsOnly',
              type: 'checkbox',
              label: 'Trusted Filters',
              options: [{ value: true, label: 'Warning Rows Only' }],
            },
          ]
        : []),
      {
        key: 'tagId',
        type: 'select',
        label: 'Tag',
        options: [
          { value: '', label: 'All' },
          ...tagOptions.map(tag => ({ value: String(tag.id), label: tag.name })),
        ],
      },
    ],
    [canUseWarningFilter, tagOptions],
  );

  const onFilterChange = useCallback(
    nextFilters => {
      const normalizedStatuses = (nextFilters.applicationTrustStatuses || [])
        .map(value => String(value))
        .filter(Boolean);
      const canUseWarningForNext = normalizedStatuses.includes(APPLICATION_TRUST_STATUS.Trusted);

      onListFiltersChange({
        applicationTrustStatuses: normalizedStatuses,
        tagId:
          nextFilters.tagId === null || nextFilters.tagId === undefined
            ? ''
            : String(nextFilters.tagId),
        warningRowsOnly: canUseWarningForNext && (nextFilters.warningRowsOnly || []).includes(true),
      });
    },
    [onListFiltersChange],
  );

  // Gates the optimistic tab-count decrement, so only membership matters here, not ordering.
  const hasFilters = useMemo(
    () =>
      selectedStatuses.length > 0 || !!selectedTagId || (warningRowsOnly && canUseWarningFilter),
    [selectedStatuses, selectedTagId, warningRowsOnly, canUseWarningFilter],
  );

  const handleSort = useCallback(
    column => {
      if (!column.sortable || !column.sortKey) {
        return;
      }
      onSortChange(
        sort.column === column.sortKey
          ? { column: column.sortKey, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
          : { column: column.sortKey, direction: 'desc' },
      );
    },
    [onSortChange, sort],
  );

  const [selectedRows, setSelectedRows] = useState(() => new Set());

  useEffect(() => {
    setSelectedRows(new Set());
  }, [listFilters, searchValue]);

  const toggleRowSelection = useCallback(row => {
    setSelectedRows(previous => {
      const next = new Set(previous);
      if (next.has(row.id)) {
        next.delete(row.id);
      } else {
        next.add(row.id);
      }
      return next;
    });
  }, []);

  const [statusModal, setStatusModal] = useState(CLOSED_STATUS_MODAL);

  // Rows are kept on close so the dialog body does not empty out mid transition.
  const closeStatusModal = useCallback(
    () => setStatusModal(previous => ({ ...previous, open: false })),
    [],
  );

  const openEditModal = useCallback(row => setStatusModal({ open: true, rows: [row] }), []);

  const openBulkModal = useCallback(() => {
    const rows = (results || []).filter(row => selectedRows.has(row.id));
    if (rows.length > 0) {
      setStatusModal({ open: true, rows });
    }
  }, [results, selectedRows]);

  const { mutate: updateStatus, isLoading: statusLoading } = useUpdateWDACStatus(
    closeStatusModal,
    hasFilters,
    query,
    customerEnvId,
  );

  const { mutateAsync: bulkUpdateStatus, isLoading: bulkStatusLoading } = useMutation(
    WDACResultsService.bulkUpdateGlobalAppStatus,
  );

  const modalRow = statusModal.rows.length === 1 ? statusModal.rows[0] : null;

  // A trust finishes on the general tab of the app, where what it built is shown.
  const saveSingleStatus = values =>
    updateStatus(
      { customerEnvId, globalApplicationVersionId: modalRow.id, ...values },
      {
        onSuccess: () => {
          if (values.status === APPLICATION_TRUST_STATUS.Trusted) {
            sessionStorage.setItem(
              WDAC_APP_PREVIOUS_PATH,
              `${window.location.pathname}${window.location.search}`,
            );
            navigate(`${PORTAL_ROUTE}/wdac-apps/${modalRow.id}/general`);
          }
        },
      },
    );

  const saveBulkStatus = useCallback(
    async (status, notes) => {
      const rows = statusModal.rows;

      try {
        await bulkUpdateStatus({
          customerEnvId,
          status,
          globalApplicationVersionIds: rows.map(row => row.id),
          notes,
        });
        enqueueSnackbar(`${rows.length.toLocaleString()} statuses updated`, {
          variant: 'success',
          autoHideDuration: 3000,
        });
        closeStatusModal();
        setSelectedRows(new Set());
        await queryClient.invalidateQueries([WDAC_RESULTS_QUERY_KEY, query, customerEnvId]);
      } catch {
        enqueueSnackbar(pagesText.somethingWentWrong, {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    },
    [
      bulkUpdateStatus,
      closeStatusModal,
      customerEnvId,
      enqueueSnackbar,
      query,
      queryClient,
      statusModal.rows,
    ],
  );

  // The list is a list of what devices report, so a row opens on the app's telemetry.
  const openAppDetails = useCallback(
    row => {
      sessionStorage.setItem(
        WDAC_APP_PREVIOUS_PATH,
        `${window.location.pathname}${window.location.search}`,
      );
      navigate(`${PORTAL_ROUTE}/wdac-apps/${row.id}/telemetry`);
    },
    [navigate],
  );

  const trimmedSearch = (searchValue || '').trim();

  const renderCell = useCallback(
    (column, row) => {
      switch (column.key) {
        case 'status':
          return (
            <TableCell key={`${row.id}-status`} sx={{ py: 0.5, px: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box
                  className="hover-checkbox"
                  sx={{
                    width: 34,
                    opacity: selectedRows.has(row.id) ? 1 : 0.35,
                    transition: 'opacity 0.2s',
                    '&:hover': {
                      opacity: 1,
                      '& .MuiSvgIcon-root path': { fill: '#7DFF92' },
                    },
                  }}>
                  <Checkbox
                    size="small"
                    checked={selectedRows.has(row.id)}
                    onChange={() => toggleRowSelection(row)}
                    onClick={event => event.stopPropagation()}
                    sx={{ '& .MuiSvgIcon-root path': { fill: '#7DFF92' } }}
                  />
                </Box>
                <StatusButton
                  status={row.status}
                  size="small"
                  sx={{ fontSize: '11px', padding: '2px 6px', minWidth: 0 }}
                  onClick={event => {
                    event.stopPropagation();
                    openEditModal(row);
                  }}>
                  {getApplicationTrustStatusLabel(row.status)}
                </StatusButton>
                <WarningIcon message={getCatalogWarning(row)} />
                {row.notes ? (
                  <Tooltip
                    title={row.notes}
                    arrow
                    componentsProps={{ tooltip: { sx: { bgcolor: '#3F3F3F', maxWidth: 320 } } }}>
                    <Box
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      width={18}
                      height={18}>
                      <Image
                        src="/static/icons/notes-notepad.svg"
                        alt="Note"
                        width={16}
                        height={16}
                      />
                    </Box>
                  </Tooltip>
                ) : null}
              </Stack>
            </TableCell>
          );
        case 'arpDisplayName':
          return (
            <TableCell key={`${row.id}-arpDisplayName`} sx={{ py: 0.5, minWidth: 0 }}>
              <Stack gap="2px" justifyContent="center" sx={{ minWidth: 0, maxWidth: '100%' }}>
                <TableCellTypography
                  sx={{
                    fontSize: '0.7rem',
                    lineHeight: 1.25,
                    m: 0,
                    display: 'block',
                    ...ELLIPSIS_SX,
                  }}>
                  <HighlightedText
                    text={row.arpDisplayName}
                    searchValue={trimmedSearch}
                    fallback="—"
                  />
                </TableCellTypography>
                <TableCellTypography
                  component="div"
                  sx={{
                    fontSize: '0.65rem',
                    color: '#9CA3AF',
                    lineHeight: 1.25,
                    m: 0,
                    ...ELLIPSIS_SX,
                  }}>
                  <HighlightedText
                    text={row.arpDisplayVersion}
                    searchValue={trimmedSearch}
                    fallback="—"
                  />
                </TableCellTypography>
              </Stack>
            </TableCell>
          );
        case 'arpPublisher':
          return (
            <TableCell key={`${row.id}-arpPublisher`} sx={{ py: 0.5, minWidth: 0 }}>
              <TableCellTypography
                sx={{ fontSize: '0.7rem', m: 0, display: 'block', ...ELLIPSIS_SX }}>
                <HighlightedText text={row.arpPublisher} searchValue={trimmedSearch} fallback="—" />
              </TableCellTypography>
            </TableCell>
          );
        default:
          return <CountCell key={`${row.id}-${column.key}`} value={row[column.key]} />;
      }
    },
    [openEditModal, selectedRows, toggleRowSelection, trimmedSearch],
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <ListActionBar
        onSearch={onSearchChange}
        defaultSearch={searchValue}
        onResetSearch={() => onSearchChange('')}
        searchPlaceholder="Search apps..."
        filters={filters}
        onFilterChange={onFilterChange}
        filterOptions={filterOptions}
        filterChips
        limitChips={5}
        filterButtonCount={totalCount}
        actionArea={
          selectedRows.size > 0 ? (
            <Button
              variant="outlined"
              onClick={openBulkModal}
              sx={{
                backgroundColor: '#3F3F3F',
                borderColor: '#2E3236',
                color: '#ffffff',
                textTransform: 'none',
                padding: '4px 14px',
                minHeight: 32,
              }}>
              {`Change Status (${selectedRows.size} app${selectedRows.size === 1 ? '' : 's'})`}
            </Button>
          ) : null
        }
        actionAreaContainerProps={{ flexShrink: 0 }}
      />

      <TableContainer roundedTop styles={{ maxHeight: `calc(100vh - 372px)` }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': { fontSize: '0.7rem' },
          }}>
          <colgroup>
            {WIN32_APPS_COLUMNS.map(column => (
              <col key={column.key} style={{ width: columnWidths[column.key] }} />
            ))}
          </colgroup>
          <TableHead sx={{ backgroundColor: '#3f3f3f', borderRadius: 0 }}>
            <TableRow>
              {WIN32_APPS_COLUMN_GROUPS.map((group, index) => (
                <TableCell
                  key={group.title || `group-${index}`}
                  colSpan={group.span}
                  align="center"
                  sx={{
                    top: 0,
                    height: GROUP_ROW_HEIGHT,
                    p: '0 8px',
                    ...(index < WIN32_APPS_COLUMN_GROUPS.length - 1 ? GROUP_DIVIDER_SX : null),
                  }}>
                  <TableHeadTypography
                    mediumScreen={mediumScreen}
                    largeScreen={largeScreen}
                    smallScreen={smallScreen}>
                    {group.title}
                  </TableHeadTypography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              {WIN32_APPS_COLUMNS.map(column => (
                <TableCell
                  key={column.key}
                  sx={{
                    top: GROUP_ROW_HEIGHT,
                    zIndex: 3,
                    p: column.key === 'status' ? '8px' : '8px 16px',
                    verticalAlign: 'middle',
                    ...(column.groupEnd ? GROUP_DIVIDER_SX : null),
                  }}>
                  <ColumnHeader
                    label={column.label}
                    tooltip={column.tooltip}
                    sortable={!!column.sortable}
                    sortActive={sort.column === column.sortKey}
                    sortDirection={sort.direction}
                    onSort={() => handleSort(column)}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
            {isLoading ? (
              <LoadingRows columnCount={WIN32_APPS_COLUMNS.length} rowCount={10} />
            ) : (
              <>
                {(results || []).map((row, index) => (
                  <TableRow
                    key={row.id}
                    ref={index === results.length - 1 ? sentryRef : null}
                    onClick={() => openAppDetails(row)}
                    sx={{ '& td': { py: 0.35 }, cursor: 'pointer' }}>
                    {WIN32_APPS_COLUMNS.map(column => renderCell(column, row))}
                  </TableRow>
                ))}
                {isFetchingNextPage && (
                  <LoadingRows columnCount={WIN32_APPS_COLUMNS.length} rowCount={3} />
                )}
                {!isFetchingNextPage && (results || []).length === 0 && (
                  <NoDataCell colSpan={WIN32_APPS_COLUMNS.length}>
                    {pagesText.noDataFound}
                  </NoDataCell>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* One app gets the form that also picks its imports; a bulk edit sets the status alone. */}
      <Win32AppStatusModal
        open={statusModal.open && !!modalRow}
        onClose={closeStatusModal}
        globalApplicationVersionId={modalRow?.id}
        summary={[modalRow?.arpDisplayName, modalRow?.arpDisplayVersion].filter(Boolean).join(' ')}
        currentStatus={modalRow?.status || ''}
        currentDate={modalRow?.trustUpdatedOn || null}
        onSave={saveSingleStatus}
        loading={statusLoading}
      />
      <UpdateStatusModal
        open={statusModal.open && statusModal.rows.length > 1}
        onClose={closeStatusModal}
        count={statusModal.rows.length}
        statusOptions={STATUS_OPTIONS}
        infoAlert={getWin32StatusNote}
        warningAlert={getBulkTrustWarning}
        prefillNote
        onSave={saveBulkStatus}
        loading={bulkStatusLoading}
      />
    </Box>
  );
};

export default memo(ResultsTable);
